import { CHANNELS } from "../shared/channels";
import { call } from "./bridge";
import { locale } from "./i18n";
import { canUse } from "./me";

// speech to text with the browser's own recognition, in the language this person picked.
// safari, chrome and edge have it, the desktop app and firefox don't

interface Recognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((event: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
}

type RecognitionCtor = new () => Recognition;

function ctor(): RecognitionCtor | undefined {
  const scope = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return scope.SpeechRecognition || scope.webkitSpeechRecognition;
}

export function dictationSupported(): boolean {
  return Boolean(ctor());
}

export interface Dictation {
  stop(): void;
}

// onText gets each finished phrase, onInterim the words still being guessed. onEnd fires once however it stopped
export function startDictation(handlers: { onText: (text: string) => void; onInterim?: (text: string) => void; onEnd: (error?: string) => void }): Dictation | null {
  const Ctor = ctor();
  if (!Ctor) return null;
  const recognition = new Ctor();
  recognition.lang = locale();
  recognition.continuous = true;
  recognition.interimResults = true;
  let failure = "";
  recognition.onresult = (event) => {
    let interim = "";
    for (let index = event.resultIndex; index < event.results.length; index++) {
      const result = event.results[index];
      const text = result[0].transcript;
      if (result.isFinal) handlers.onText(text.trim());
      else interim += text;
    }
    handlers.onInterim?.(interim.trim());
  };
  recognition.onerror = (event) => {
    // a quiet stretch isn't a failure
    if (event.error !== "no-speech" && event.error !== "aborted") failure = event.error;
  };
  recognition.onend = () => {
    handlers.onInterim?.("");
    handlers.onEnd(failure || undefined);
  };
  try {
    recognition.start();
  } catch {
    return null;
  }
  return { stop: () => recognition.stop() };
}

// a mic button that types what it hears onto the end of a textarea, and fires input so autosave and fit keep working
export function wireDictateButton(button: HTMLButtonElement, field: HTMLTextAreaElement | HTMLInputElement, onStatus?: (text: string) => void): void {
  if (!dictationSupported()) {
    button.hidden = true;
    return;
  }
  let active: Dictation | null = null;
  let heard = false;
  const paint = (on: boolean) => {
    button.classList.toggle("recording", on);
    button.setAttribute("aria-pressed", String(on));
  };
  button.addEventListener("click", () => {
    if (active) {
      active.stop();
      return;
    }
    active = startDictation({
      onText: (text) => {
        if (!text) return;
        heard = true;
        const gap = !field.value || /\s$/.test(field.value) ? "" : " ";
        field.value += gap + text;
        field.dispatchEvent(new Event("input", { bubbles: true }));
      },
      onInterim: (text) => onStatus?.(text),
      onEnd: (error) => {
        active = null;
        paint(false);
        // a dictation that caught words counts toward achievements
        if (heard && canUse("awards", "edit")) void call(CHANNELS.awardsEvent, "voice_used").catch(() => undefined);
        heard = false;
        if (error) onStatus?.(error === "not-allowed" || error === "service-not-allowed" ? "mic blocked" : "could not hear");
      }
    });
    paint(Boolean(active));
  });
}
